import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { API_ENDPOINT } from '../utils/api';
import { X, Activity, AlertTriangle, FileText, FileCheck, TrendingUp } from 'lucide-react';

function HistoricAnalysisModal({ reportId, onClose }) {
  const { organization } = useContext(AuthContext);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!organization?.organizationId) {
        console.error('Organization not loaded');
        setLoading(false);
        return;
      }

      try {
        const url = `${API_ENDPOINT}/dashboard/reports/${reportId}?organization_id=${organization.organizationId}`;
        
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          setAnalysis(data);
        } else {
          console.error('Response not OK:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAnalysis();
  }, [reportId, organization]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Activity className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-500" />
          <p>Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (!analysis?.key_insights) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold mb-2 text-center">Analysis Not Available</h3>
          <p className="text-gray-600 mb-4 text-center">This report may still be processing.</p>
          <button onClick={onClose} className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  const insights = analysis.key_insights;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historic AAR Analysis</h2>
            <p className="text-sm text-gray-600 mt-1">AI-generated insights from uploaded document</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 hover:bg-white rounded-full transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {insights.key_findings?.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Key Findings
              </h3>
              <ul className="space-y-2">
                {insights.key_findings.map((item, i) => (
                  <li key={i} className="text-blue-800 text-sm flex gap-2">
                    <span className="text-blue-500 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.strengths?.length > 0 && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <FileCheck className="w-5 h-5" />
                Strengths
              </h3>
              <ul className="space-y-2">
                {insights.strengths.map((item, i) => (
                  <li key={i} className="text-green-800 text-sm flex gap-2">
                    <span className="text-green-500 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.weaknesses?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-4 border border-red-100">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Areas for Improvement
              </h3>
              <ul className="space-y-2">
                {insights.weaknesses.map((item, i) => (
                  <li key={i} className="text-red-800 text-sm flex gap-2">
                    <span className="text-red-500 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.recommendations?.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Recommendations
              </h3>
              <ul className="space-y-2">
                {insights.recommendations.map((item, i) => (
                  <li key={i} className="text-purple-800 text-sm flex gap-2">
                    <span className="text-purple-500 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.lessons_learned?.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
              <h3 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Lessons Learned
              </h3>
              <ul className="space-y-2">
                {insights.lessons_learned.map((item, i) => (
                  <li key={i} className="text-yellow-800 text-sm flex gap-2">
                    <span className="text-yellow-500 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button 
            onClick={onClose} 
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default HistoricAnalysisModal;
